import {POLL_INTERVAL_MS, SitePreviewView, viewState} from '../../../core/site-preview/site-preview.core.js';
import {ErrorCode, NotificationType} from '../../../core/vocabulary.js';
import {t} from '../../../i18n/i18n.js';
import {errorText} from './error-text.js';

/** Errors the bar words for the preview instead of with the generic copy written for the page form. */
const SITE_PREVIEW_ERROR_KEYS = Object.freeze({
    [ErrorCode.NO_DRAFTS]: 'sitePreview.noDrafts',
    [ErrorCode.NO_CHANGES]: 'sitePreview.noChanges',
});

/**
 * Builds the preview of the site when the site preview bar asks for one, then checks the build of
 * the commit it answered every few seconds until it is ready, failed or slow. A new request or a sign-out stops the checks of the
 * previous one. Failures stay in the bar, except an expired session, which signs out. A sign-out
 * and a publication (`drafts-published`, after which the preview is gone) put the bar back to idle.
 */
export class SitePreviewEventAdapter {
    #pageApiAdapter;
    #notificationComponent;
    #onSessionExpired;
    /** Bumped by every request and by disconnect; work of an earlier request leaves the bar alone. */
    #generation = 0;
    #timer = null;
    /** The bar of the latest request, put back to idle when its preview no longer applies. */
    #bar = null;

    /**
     * @param {import('../driven/page.api-adapter.js').PageApiAdapter} pageApiAdapter
     * @param {HTMLElement} notificationComponent
     * @param {() => void} onSessionExpired signs out once a request finds the session expired
     */
    constructor(pageApiAdapter, notificationComponent, onSessionExpired) {
        this.#pageApiAdapter = pageApiAdapter;
        this.#notificationComponent = notificationComponent;
        this.#onSessionExpired = onSessionExpired;
    }

    connect() {
        document.addEventListener('site-preview-request', this.#onRequest);
        document.addEventListener('drafts-published', this.#reset);
    }

    disconnect() {
        document.removeEventListener('site-preview-request', this.#onRequest);
        document.removeEventListener('drafts-published', this.#reset);
        this.#reset();
    }

    /** @param {import('../driven/page.api-adapter.js').PageApiAdapter} pageApiAdapter */
    updateApiAdapter(pageApiAdapter) {
        this.#pageApiAdapter = pageApiAdapter;
    }

    #cancel() {
        this.#generation++;
        clearTimeout(this.#timer);
        this.#timer = null;
    }

    /** Stops any check and shows the bar idle again. */
    #reset = () => {
        this.#cancel();
        if (this.#bar) {
            this.#bar.state = {kind: SitePreviewView.IDLE, url: null};
        }
    };

    #onRequest = async (event) => {
        const bar = event.composedPath()[0];
        this.#bar = bar;
        this.#cancel();
        const generation = this.#generation;
        const startedAt = Date.now();
        bar.state = {kind: SitePreviewView.BUILDING, url: null};
        let commit;
        try {
            commit = (await this.#pageApiAdapter.startSitePreview())?.commit ?? null;
        } catch (error) {
            this.#fail(bar, generation, error);
            return;
        }
        if (generation === this.#generation) {
            this.#scheduleCheck(bar, generation, startedAt, commit);
        }
    };

    #scheduleCheck(bar, generation, startedAt, commit) {
        this.#timer = setTimeout(() => this.#check(bar, generation, startedAt, commit), POLL_INTERVAL_MS);
    }

    async #check(bar, generation, startedAt, commit) {
        this.#timer = null;
        let state;
        try {
            state = viewState(await this.#pageApiAdapter.getSitePreview(commit), Date.now() - startedAt);
        } catch (error) {
            this.#fail(bar, generation, error);
            return;
        }
        if (generation !== this.#generation) return;
        bar.state = state;
        if (state.kind === SitePreviewView.BUILDING) {
            this.#scheduleCheck(bar, generation, startedAt, commit);
        }
    }

    #fail(bar, generation, error) {
        if (generation !== this.#generation) return;
        if (error?.status === 401) {
            bar.state = {kind: SitePreviewView.IDLE, url: null};
            // The toast stays up on the login screen and says why the user was signed out.
            this.#notificationComponent.show(t('global.sessionExpired'), NotificationType.ERROR);
            this.#onSessionExpired();
            return;
        }
        const key = SITE_PREVIEW_ERROR_KEYS[error?.code];
        const message = key ? t(key) : errorText(error);
        bar.state = {kind: SitePreviewView.ERROR, url: null, message};
    }
}
